import { useState, useEffect, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, ChevronDown, ChevronUp, Shield, BarChart3, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const COOKIE_CONSENT_KEY = "ulture_cookie_consent";

interface CookieConsent {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const bannerText = {
  en: {
    title: "We use cookies",
    description:
      "We use cookies to improve your experience, analyse traffic, and personalise content. You can manage your preferences or accept all cookies.",
    learnMore: "Learn more about our Cookie Policy",
    essential: "Essential",
    essentialDesc: "Required for the site to function. Cannot be disabled.",
    analytics: "Analytics",
    analyticsDesc: "Help us understand how visitors interact with our site.",
    marketing: "Marketing",
    marketingDesc: "Used to deliver personalised advertisements.",
    reject: "Reject non-essential",
    configure: "Configure",
    acceptAll: "Accept all",
    save: "Save preferences",
    alwaysOn: "Always on",
  },
  es: {
    title: "Usamos cookies",
    description:
      "Usamos cookies para mejorar tu experiencia, analizar el tráfico y personalizar contenido. Puedes gestionar tus preferencias o aceptar todas las cookies.",
    learnMore: "Saber más sobre nuestra Política de Cookies",
    essential: "Esenciales",
    essentialDesc: "Necesarias para el funcionamiento del sitio. No se pueden deshabilitar.",
    analytics: "Analíticas",
    analyticsDesc: "Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio.",
    marketing: "Marketing",
    marketingDesc: "Se utilizan para mostrar anuncios personalizados.",
    reject: "Rechazar no esenciales",
    configure: "Configurar",
    acceptAll: "Aceptar todas",
    save: "Guardar preferencias",
    alwaysOn: "Siempre activas",
  },
  it: {
    title: "Usiamo i cookie",
    description:
      "Usiamo i cookie per migliorare la tua esperienza, analizzare il traffico e personalizzare i contenuti. Puoi gestire le tue preferenze o accettare tutti i cookie.",
    learnMore: "Scopri di più nella nostra informativa sui cookie",
    essential: "Necessari",
    essentialDesc: "Indispensabili per il funzionamento del sito. Non possono essere disattivati.",
    analytics: "Analitici",
    analyticsDesc: "Ci aiutano a capire come i visitatori interagiscono con il nostro sito.",
    marketing: "Marketing",
    marketingDesc: "Servono a mostrarti annunci personalizzati.",
    reject: "Rifiuta tutti",
    configure: "Personalizza",
    acceptAll: "Accetta tutti",
    save: "Salva preferenze",
    alwaysOn: "Sempre attivi",
  },
  fr: {
    title: "Nous utilisons des cookies",
    description:
      "Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. Vous pouvez gérer vos préférences ou accepter tous les cookies.",
    learnMore: "En savoir plus sur notre politique de cookies",
    essential: "Essentiels",
    essentialDesc: "Indispensables au fonctionnement du site. Ils ne peuvent pas être désactivés.",
    analytics: "Mesure d'audience",
    analyticsDesc: "Nous aident à comprendre comment les visiteurs utilisent notre site.",
    marketing: "Marketing",
    marketingDesc: "Utilisés pour vous proposer des publicités personnalisées.",
    reject: "Tout refuser",
    configure: "Personnaliser",
    acceptAll: "Tout accepter",
    save: "Enregistrer mes préférences",
    alwaysOn: "Toujours actifs",
  },
  nl: {
    title: "We gebruiken cookies",
    description:
      "We gebruiken cookies om je ervaring te verbeteren, het websiteverkeer te analyseren en content te personaliseren. Je kunt je voorkeuren beheren of alle cookies accepteren.",
    learnMore: "Lees meer in ons cookiebeleid",
    essential: "Noodzakelijk",
    essentialDesc: "Nodig om de website te laten werken. Deze kunnen niet worden uitgeschakeld.",
    analytics: "Analytisch",
    analyticsDesc: "Helpen ons te begrijpen hoe bezoekers onze website gebruiken.",
    marketing: "Marketing",
    marketingDesc: "Worden gebruikt om gepersonaliseerde advertenties te tonen.",
    reject: "Alles weigeren",
    configure: "Aanpassen",
    acceptAll: "Alles accepteren",
    save: "Voorkeuren opslaan",
    alwaysOn: "Altijd actief",
  },
  ar: {
    title: "نستخدم ملفات تعريف الارتباط",
    description:
      "نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل حركة الزيارات وتخصيص المحتوى. يمكنك إدارة تفضيلاتك أو قبول جميع ملفات تعريف الارتباط.",
    learnMore: "مزيد من المعلومات حول سياسة ملفات تعريف الارتباط",
    essential: "ضرورية",
    essentialDesc: "لازمة لعمل الموقع، ولا يمكن تعطيلها.",
    analytics: "تحليلية",
    analyticsDesc: "تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.",
    marketing: "تسويقية",
    marketingDesc: "تُستخدم لعرض إعلانات مخصّصة.",
    reject: "رفض غير الضرورية",
    configure: "تخصيص",
    acceptAll: "قبول الكل",
    save: "حفظ التفضيلات",
    alwaysOn: "مفعّلة دائمًا",
  },
  zh: {
    title: "我们使用 Cookie",
    description:
      "我们使用 Cookie 来改善你的浏览体验、分析网站流量并提供个性化内容。你可以管理偏好设置，或接受全部 Cookie。",
    learnMore: "详细了解我们的 Cookie 政策",
    essential: "必要 Cookie",
    essentialDesc: "网站正常运行所必需，无法停用。",
    analytics: "分析 Cookie",
    analyticsDesc: "帮助我们了解访客如何使用本网站。",
    marketing: "营销 Cookie",
    marketingDesc: "用于向你展示个性化广告。",
    reject: "拒绝非必要 Cookie",
    configure: "自定义",
    acceptAll: "全部接受",
    save: "保存偏好设置",
    alwaysOn: "始终启用",
  },
  // pt solo existe en el contexto legado (no en el header), p. ej. al llegar desde un email en portugués.
  pt: {
    title: "Usamos cookies",
    description:
      "Usamos cookies para melhorar a sua experiência, analisar o tráfego e personalizar conteúdos. Pode gerir as suas preferências ou aceitar todos os cookies.",
    learnMore: "Saiba mais na nossa Política de Cookies",
    essential: "Essenciais",
    essentialDesc: "Necessários para o funcionamento do site. Não podem ser desativados.",
    analytics: "Analíticos",
    analyticsDesc: "Ajudam-nos a perceber como os visitantes interagem com o nosso site.",
    marketing: "Marketing",
    marketingDesc: "Utilizados para mostrar anúncios personalizados.",
    reject: "Rejeitar não essenciais",
    configure: "Configurar",
    acceptAll: "Aceitar todos",
    save: "Guardar preferências",
    alwaysOn: "Sempre ativos",
  },
};

type BannerLang = keyof typeof bannerText;

// Idioma del banner = el que el visitante eligió en el header de la web
// (localStorage "ulture_lang", la misma fuente que LandingLanguage); si aún no
// ha elegido, el del contexto legado (useLanguage); y si no, español.
const SITE_LANGS = ["es", "en", "it", "fr", "nl", "ar", "zh"];

function readSiteLang(): string | null {
  try {
    return localStorage.getItem("ulture_lang");
  } catch {
    return null;
  }
}

// El header guarda el idioma en localStorage y cambia <html lang>. localStorage no
// avisa dentro de la misma pestaña, así que se escucha <html lang> para repintar el
// banner al momento, aunque el contexto legado no cambie.
function subscribeHtmlLang(onChange: () => void) {
  const obs = new MutationObserver(onChange);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });
  return () => obs.disconnect();
}

function pickBannerLang(siteLang: string | null, legacyLang: string): BannerLang {
  if (siteLang && SITE_LANGS.includes(siteLang)) return siteLang as BannerLang;
  if (Object.prototype.hasOwnProperty.call(bannerText, legacyLang)) return legacyLang as BannerLang;
  return "es";
}

function getStoredConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookieConsent;
  } catch {
    return null;
  }
}

function storeConsent(consent: Omit<CookieConsent, "timestamp">) {
  localStorage.setItem(
    COOKIE_CONSENT_KEY,
    JSON.stringify({ ...consent, timestamp: Date.now() })
  );
}

export function useCookieConsent() {
  const [consent, setConsentState] = useState<CookieConsent | null>(() =>
    getStoredConsent()
  );

  const setConsent = (c: Omit<CookieConsent, "timestamp">) => {
    storeConsent(c);
    setConsentState({ ...c, timestamp: Date.now() });
  };

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setConsentState(null);
  };

  return { consent, setConsent, resetConsent };
}

export default function CookieBanner() {
  const { language } = useLanguage();
  const siteLang = useSyncExternalStore(subscribeHtmlLang, readSiteLang, () => null);
  const lang = pickBannerLang(siteLang, language);
  const t = bannerText[lang];
  // Dirección del banner (no la de <html>, que en alguna página puede no coincidir):
  // con ella se desplaza el círculo de los interruptores hacia dentro.
  const isRtl = lang === "ar";
  const knobOn = isRtl ? "-translate-x-5" : "translate-x-5";
  const knobOff = isRtl ? "-translate-x-0.5" : "translate-x-0.5";
  // The admin panel isn't part of the public site: no banner over it.
  const onAdmin = useLocation().pathname.startsWith("/admin");

  const [visible, setVisible] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      // Slight delay so page renders first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const c = { essential: true as const, analytics: true, marketing: true };
    storeConsent(c);
    window.dispatchEvent(new CustomEvent("ulture:consent-changed", { detail: { ...c, timestamp: Date.now() } }));
    setVisible(false);
  };

  const handleReject = () => {
    const c = { essential: true as const, analytics: false, marketing: false };
    storeConsent(c);
    window.dispatchEvent(new CustomEvent("ulture:consent-changed", { detail: { ...c, timestamp: Date.now() } }));
    setVisible(false);
  };

  const handleSave = () => {
    const c = { essential: true as const, analytics, marketing };
    storeConsent(c);
    window.dispatchEvent(new CustomEvent("ulture:consent-changed", { detail: { ...c, timestamp: Date.now() } }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && !onAdmin && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-cookie-banner=""
          lang={lang}
          dir={isRtl ? "rtl" : "ltr"}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[440px] z-[9999]"
        >
          <div className="bg-[hsl(220,20%,10%)] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between p-5 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-4 h-4 text-white" />
                </div>
                <p className="text-white font-semibold text-base">{t.title}</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-5 pb-4">
              <p className="text-white/60 text-sm leading-relaxed">
                {t.description}{" "}
                <Link
                  to="/cookie-policy"
                  className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                >
                  {t.learnMore}
                </Link>
              </p>

              {/* Configure panel */}
              <AnimatePresence>
                {configOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                      {/* Essential */}
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Shield className="w-3.5 h-3.5 text-emerald-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-white text-sm font-medium">{t.essential}</span>
                            <span className="text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded">{t.alwaysOn}</span>
                          </div>
                          <p className="text-white/50 text-xs mt-0.5">{t.essentialDesc}</p>
                        </div>
                      </div>

                      {/* Analytics */}
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <BarChart3 className="w-3.5 h-3.5 text-cyan-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-white text-sm font-medium">{t.analytics}</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={analytics}
                              aria-label={t.analytics}
                              onClick={() => setAnalytics((v) => !v)}
                              className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                                analytics ? "bg-cyan-500" : "bg-white/20"
                              }`}
                            >
                              {/* Círculo anclado al inicio (start-0); en árabe (RTL) se desplaza en espejo. Igual en Marketing. */}
                              <span
                                className={`absolute top-0.5 start-0 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                  analytics ? knobOn : knobOff
                                }`}
                              />
                            </button>
                          </div>
                          <p className="text-white/50 text-xs mt-0.5">{t.analyticsDesc}</p>
                        </div>
                      </div>

                      {/* Marketing */}
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Megaphone className="w-3.5 h-3.5 text-teal-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-white text-sm font-medium">{t.marketing}</span>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={marketing}
                              aria-label={t.marketing}
                              onClick={() => setMarketing((v) => !v)}
                              className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                                marketing ? "bg-cyan-500" : "bg-white/20"
                              }`}
                            >
                              <span
                                className={`absolute top-0.5 start-0 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                                  marketing ? knobOn : knobOff
                                }`}
                              />
                            </button>
                          </div>
                          <p className="text-white/50 text-xs mt-0.5">{t.marketingDesc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 space-y-2">
              {configOpen ? (
                <Button
                  onClick={handleSave}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium h-10"
                >
                  {t.save}
                </Button>
              ) : (
                <Button
                  onClick={handleAcceptAll}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-medium h-10"
                >
                  {t.acceptAll}
                </Button>
              )}

              {/* flex-wrap: a 320 px "Rechazar no esenciales" + "Configurar" no cabían en una línea */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  onClick={handleReject}
                  className="flex-1 min-w-[9.5rem] text-white/60 hover:text-white hover:bg-white/5 h-9 text-sm"
                >
                  {t.reject}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfigOpen((v) => !v)}
                  className="flex-1 min-w-[7rem] text-white/60 hover:text-white hover:bg-white/5 h-9 text-sm flex items-center justify-center gap-1"
                >
                  {t.configure}
                  {configOpen ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
